import { useState, useMemo, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { IoSearchOutline, IoCloseOutline, IoCalendarOutline, IoFunnelOutline, IoArrowUpOutline, IoArrowDownOutline } from "react-icons/io5";
import TabOptions from "../../components/TabOptions";
import { OcurrenceItem } from "../../components/OcurrenceItem";
import { WorkOrderItem } from "../../components/WorkOrderItem";
import { useInfiniteOcurrences } from "@/hooks/useOcurrences";
import { useGetWorkOrders } from "@/hooks/useWorkOrdersApi";
import { getUserData } from "@/utils/jwt";
import type { Ocurrence } from "@/types/Ocurrence";
import type { WorkOrder } from "@/types/WorkOrder";
import { Spinner } from "@/components/Spinner";

const STATUS_MAP: Record<string, string[]> = {
  Todas: [],
  "Aguardando Análise": ["AWAITING_ANALYSIS"],
  "Em Andamento": ["IN_PROGRESS"],
  Concluída: ["RESOLVED"],
  Arquivada: ["FILED"],
  Devolvida: ["RETURNED"],
};

const WO_STATUS_MAP: Record<string, string[]> = {
  Todas: [],
  Pendente: ["PENDING"],
  "Em Andamento": ["IN_PROGRESS"],
  "Aguardando Validação": ["AWAITING_VALIDATION"],
  Concluída: ["COMPLETED"],
};

export const Ocurrences = () => {
  const [selectTabOption, setSelectTabOption] = useState("Todas");
  const [protocolSearch, setProtocolSearch] = useState("");
  const [debouncedProtocol, setDebouncedProtocol] = useState("");
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Filtros adicionais para agentes
  const [showFilters, setShowFilters] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [prioritySort, setPrioritySort] = useState<"asc" | "desc" | "">("");

  // Detecta o role do usuário
  const userData = getUserData();
  const isFieldAgent = userData?.role === "AGENT";

  // Debounce para busca de protocolo (500ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedProtocol(protocolSearch);
    }, 500);

    return () => clearTimeout(timer);
  }, [protocolSearch]);

  const filters = useMemo(() => {
    const statusFilter = isFieldAgent
      ? WO_STATUS_MAP[selectTabOption] || []
      : STATUS_MAP[selectTabOption] || [];

    return isFieldAgent
      ? {
          status: statusFilter[0] as any,
          search: debouncedProtocol,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          sortByPriority: prioritySort || undefined,
        }
      : {
          status: statusFilter,
          search: debouncedProtocol,
          includeFollowing: true,
        };
  }, [selectTabOption, debouncedProtocol, isFieldAgent, startDate, endDate, prioritySort]);

  // Hook para cidadãos
  const {
    data: occurrencesData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingOccurrences,
  } = useInfiniteOcurrences(!isFieldAgent ? filters : undefined);

  // Hook para agentes (API real)
  const { data: workOrdersResponse, isLoading: isLoadingWorkOrders } =
    useGetWorkOrders(isFieldAgent ? filters : undefined);

  const isLoading = isFieldAgent ? isLoadingWorkOrders : isLoadingOccurrences;

  // Intersection Observer para scroll infinito (apenas para cidadãos)
  useEffect(() => {
    if (isFieldAgent) return; // Agentes não têm scroll infinito

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, isFieldAgent]);

  // Flatten das páginas (para cidadãos) ou dados diretos (para agentes)
  const allOccurrences = isFieldAgent
    ? []
    : occurrencesData?.pages.flatMap((page) => page.data) || [];
  const allWorkOrders = isFieldAgent ? workOrdersResponse?.data || [] : [];
  const totalCount = isFieldAgent
    ? workOrdersResponse?.meta?.total || 0
    : occurrencesData?.pages[0]?.meta?.total || 0;

  const tabOptions = isFieldAgent
    ? ["Todas", "Pendente", "Em Andamento", "Aguardando Validação", "Concluída"]
    : [
        "Todas",
        "Aguardando Análise",
        "Em Andamento",
        "Concluída",
        "Arquivada",
        "Devolvida",
      ];

  const pageTitle = isFieldAgent
    ? "Minhas Ordens de Serviço"
    : "Minhas Ocorrências";
  const itemLabel = isFieldAgent
    ? totalCount === 1
      ? "ordem de serviço"
      : "ordens de serviço"
    : totalCount === 1
      ? "ocorrência encontrada"
      : "ocorrências encontradas";

  return (
    <div className="bg-[#f5f5f5] min-h-screen pt-20 pb-20">
      <div className="w-full flex items-center justify-center lg:mt-5 flex-col">
        <div className="w-[95%] lg:w-[98%] max-w-4xl">
          {/* Header com título e contador */}
          <div className="mb-4">
            <h1 className="text-2xl font-bold text-gray-800">{pageTitle}</h1>
            {totalCount !== undefined && (
              <p className="text-sm text-gray-600 mt-1">
                {totalCount} {itemLabel}
              </p>
            )}
          </div>

          {/* Campo de busca por protocolo */}
          <div className="mb-4">
            <div className="relative">
              <IoSearchOutline className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl" />
              <input
                type="text"
                value={protocolSearch}
                onChange={(e) => setProtocolSearch(e.target.value)}
                placeholder="Buscar por número de protocolo..."
                className="w-full pl-12 pr-12 py-3.5 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))] focus:border-transparent text-sm shadow-sm"
              />
              {protocolSearch && (
                <button
                  onClick={() => setProtocolSearch("")}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <IoCloseOutline className="text-xl" />
                </button>
              )}
            </div>
          </div>

          {/* Filtros modernos */}
          <div className="mb-5">
            <TabOptions
              options={tabOptions}
              selectedOption={selectTabOption}
              onSelectOption={setSelectTabOption}
            />
          </div>

          {/* Filtros adicionais para agentes */}
          {isFieldAgent && (
            <div className="mb-4">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="w-full flex items-center justify-between bg-white px-4 py-3 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all"
              >
                <div className="flex items-center gap-2">
                  <IoFunnelOutline className="text-lg text-gray-600" />
                  <span className="font-semibold text-gray-700">
                    Filtros Avançados
                  </span>
                  {(startDate || endDate || prioritySort) && (
                    <span className="bg-primary text-white text-xs px-2 py-0.5 rounded-full">
                      Ativo
                    </span>
                  )}
                </div>
                <motion.div
                  animate={{ rotate: showFilters ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <IoArrowDownOutline className="text-gray-600" />
                </motion.div>
              </button>

              <AnimatePresence>
                {showFilters && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="bg-white border border-gray-200 rounded-xl p-4 mt-2 space-y-4 shadow-sm">
                      {/* Filtro de Data */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                          <IoCalendarOutline className="text-primary" />
                          Período de Criação
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">
                              Data Início
                            </label>
                            <input
                              type="date"
                              value={startDate}
                              onChange={(e) => setStartDate(e.target.value)}
                              max={endDate || undefined}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">
                              Data Fim
                            </label>
                            <input
                              type="date"
                              value={endDate}
                              onChange={(e) => setEndDate(e.target.value)}
                              min={startDate || undefined}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Filtro de Prioridade */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Ordenar por Prioridade
                        </label>
                        <div className="grid grid-cols-3 gap-2">
                          <button
                            onClick={() => setPrioritySort("")}
                            className={`px-3 py-2 rounded-lg font-medium text-sm transition-all ${
                              prioritySort === ""
                                ? "bg-primary text-white shadow-md"
                                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            }`}
                          >
                            Padrão
                          </button>
                          <button
                            onClick={() => setPrioritySort("desc")}
                            className={`px-3 py-2 rounded-lg font-medium text-sm transition-all flex items-center justify-center gap-1 ${
                              prioritySort === "desc"
                                ? "bg-primary text-white shadow-md"
                                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            }`}
                          >
                            <IoArrowUpOutline />
                            Maior
                          </button>
                          <button
                            onClick={() => setPrioritySort("asc")}
                            className={`px-3 py-2 rounded-lg font-medium text-sm transition-all flex items-center justify-center gap-1 ${
                              prioritySort === "asc"
                                ? "bg-primary text-white shadow-md"
                                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            }`}
                          >
                            <IoArrowDownOutline />
                            Menor
                          </button>
                        </div>
                      </div>

                      {/* Botão Limpar Filtros */}
                      {(startDate || endDate || prioritySort) && (
                        <button
                          onClick={() => {
                            setStartDate("");
                            setEndDate("");
                            setPrioritySort("");
                          }}
                          className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors text-sm"
                        >
                          Limpar Filtros
                        </button>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          <div className="gap-2 flex flex-col mb-10">
            {isLoading ? (
              <div className="flex justify-center py-8">
                <Spinner className="w-8 h-8" />
              </div>
            ) : isFieldAgent ? (
              // Renderiza Work Orders para agentes
              allWorkOrders.length > 0 ? (
                <>
                  {allWorkOrders.map((workOrder: WorkOrder, index: number) => (
                    <motion.div
                      key={workOrder.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{
                        duration: 0.3,
                        delay: Math.min(0.1 * index, 1),
                      }}
                    >
                      <WorkOrderItem data={workOrder} />
                    </motion.div>
                  ))}
                </>
              ) : (
                <div className="bg-white rounded-xl shadow-sm p-8 text-center">
                  <IoSearchOutline className="text-5xl text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-600 font-medium">
                    {debouncedProtocol
                      ? `Nenhuma OS encontrada com o protocolo "${debouncedProtocol}"`
                      : "Nenhuma OS atribuída a você"}
                  </p>
                </div>
              )
            ) : // Renderiza Ocorrências para cidadãos
            allOccurrences.length > 0 ? (
              <>
                {allOccurrences.map((ocurrence: Ocurrence, index: number) => (
                  <motion.div
                    key={ocurrence.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{
                      duration: 0.3,
                      delay: Math.min(0.1 * index, 1),
                    }}
                  >
                    <OcurrenceItem data={ocurrence} />
                  </motion.div>
                ))}

                {/* Elemento observer para scroll infinito */}
                <div
                  ref={loadMoreRef}
                  className="h-10 flex items-center justify-center"
                >
                  {isFetchingNextPage && <Spinner className="w-6 h-6" />}
                </div>
              </>
            ) : (
              <div className="bg-white rounded-xl shadow-sm p-8 text-center">
                <IoSearchOutline className="text-5xl text-gray-300 mx-auto mb-3" />
                <p className="text-gray-600 font-medium">
                  {debouncedProtocol
                    ? `Nenhuma ocorrência encontrada com o protocolo "${debouncedProtocol}"`
                    : "Nenhuma ocorrência encontrada"}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
